/**
 * Centralised error handling for Supabase / API errors.
 *
 * Logs the original error to the console for debugging,
 * then re-throws a user-friendly message in Portuguese.
 */

const PG_ERROR_MESSAGES: Record<string, string> = {
  '23505': 'Registro duplicado. Verifique os dados e tente novamente.',
  '23503': 'Não é possível realizar esta operação pois existem dados relacionados.',
  '23502': 'Campos obrigatórios não foram preenchidos.',
  '23514': 'Os dados informados são inválidos.',
  '42501': 'Você não tem permissão para realizar esta operação.',
  '42P01': 'Erro interno do sistema. Tente novamente mais tarde.',
  'PGRST301': 'Sua sessão expirou. Faça login novamente.',
};

const PATTERN_MESSAGES: [RegExp, string][] = [
  [/duplicate key|unique constraint/i, 'Registro duplicado. Verifique os dados e tente novamente.'],
  [/foreign key|violates foreign key/i, 'Não é possível realizar esta operação pois existem dados relacionados.'],
  [/not.found|PGRST116/i, 'Registro não encontrado.'],
  [/invalid_mime_type|mime type .* not supported|application\/octet-stream/i, 'A foto selecionada não é compatível. Envie uma imagem JPG, PNG ou WEBP.'],
  [/JWT|token|expired|unauthorized/i, 'Sua sessão expirou. Faça login novamente.'],
  [/network|fetch|ECONNREFUSED|ERR_NETWORK/i, 'Erro de conexão. Verifique sua internet e tente novamente.'],
  [/timeout|ETIMEDOUT/i, 'A operação demorou muito. Tente novamente.'],
  [/permission denied|row-level security/i, 'Você não tem permissão para realizar esta operação.'],
];

function isFriendlyMessage(msg: string): boolean {
  const technicalPatterns = [
    /duplicate key/i, /constraint/i, /violates/i, /PGRST/i,
    /relation ".*" does not exist/i, /column ".*"/i,
    /syntax error/i, /stack trace/i, /at .*\.ts:/i,
    /permission denied for/i, /row-level security/i,
  ];
  return !technicalPatterns.some((p) => p.test(msg));
}

/**
 * Call this from catch blocks in services or UI components.
 *
 * @param error  The caught error (unknown type)
 * @param context  Short description used as fallback, e.g. "cadastrar aluna"
 * @returns never — always throws a user-friendly Error
 */
export function handleServiceError(error: unknown, context: string): never {
  console.error(`[${context}]`, error);

  // Supabase PostgrestError — has `code` and `message`
  if (error && typeof error === 'object' && 'code' in error) {
    const code = String((error as any).code);
    const friendly = PG_ERROR_MESSAGES[code];
    if (friendly) throw new Error(friendly);
  }

  // Check message patterns
  const rawMessage =
    error instanceof Error
      ? error.message
      : typeof error === 'object' && error !== null && 'message' in error
        ? String((error as any).message)
        : '';

  if (rawMessage) {
    for (const [pattern, friendly] of PATTERN_MESSAGES) {
      if (pattern.test(rawMessage)) throw new Error(friendly);
    }

    // If the message already looks user-friendly (Portuguese, no SQL terms), pass through
    if (isFriendlyMessage(rawMessage)) throw new Error(rawMessage);
  }

  // Fallback
  throw new Error(`Erro ao ${context}. Tente novamente.`);
}
